import React, { useState, useEffect } from 'react'
import { Modal, Button, Row, Col, Badge, Form, Alert, Spinner } from 'react-bootstrap'
import {
  useAdminTableColumn,
  useUpdateAdminTableColumn
} from 'api/hooks/use-admin-table-columns'

const TableColumnModal = ({ tableColumnId, mode, onClose, onSaved }) => {
  const show = !!tableColumnId
  const isEditing = mode === 'edit'
  const { data: tableColumn, isLoading } = useAdminTableColumn({ id: tableColumnId })
  const updateMutation = useUpdateAdminTableColumn({
    id: tableColumnId,
    options: { onSuccess: () => { onSaved?.(); onClose() } }
  })

  const [formData, setFormData] = useState({})

  useEffect(() => {
    if (tableColumn && isEditing) {
      setFormData({
        name: tableColumn.name || '',
        database_column_name: tableColumn.database_column_name || '',
        accessor_key: tableColumn.accessor_key || '',
        description: tableColumn.description || '',
        display_as: tableColumn.display_as || '',
        filter_type: tableColumn.filter_type || '',
        grouping: tableColumn.grouping || '',
        source_association: tableColumn.source_association || '',
        source_attribute: tableColumn.source_attribute || '',
        is_default: tableColumn.is_default ?? false,
        is_sortable: tableColumn.is_sortable ?? false,
        is_selectable: tableColumn.is_selectable ?? false
      })
    }
  }, [tableColumn, isEditing])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    updateMutation.mutate(formData)
  }

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Table Column' : 'View Table Column'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : !tableColumn ? (
          <Alert variant="warning">Table column not found.</Alert>
        ) : isEditing ? (
          <Form onSubmit={handleSubmit}>
            {updateMutation.error && (
              <Alert variant="danger" className="mb-3">{updateMutation.error.message}</Alert>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Database Column Name</Form.Label>
                  <Form.Control type="text" name="database_column_name" value={formData.database_column_name} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Accessor Key</Form.Label>
                  <Form.Control type="text" name="accessor_key" value={formData.accessor_key} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display As</Form.Label>
                  <Form.Control type="text" name="display_as" value={formData.display_as} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Filter Type</Form.Label>
                  <Form.Control type="text" name="filter_type" value={formData.filter_type} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Grouping</Form.Label>
                  <Form.Control type="text" name="grouping" value={formData.grouping} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            {/* Read this column from a record the transaction belongs to instead of
                the section's detail table (Vendor Type reads vendors.general_type).
                Blank means the detail table. See ColumnSource on the API side. */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Association</Form.Label>
                  <Form.Select name="source_association" value={formData.source_association} onChange={handleChange}>
                    <option value="">Detail table (default)</option>
                    <option value="vendor">Vendor</option>
                    <option value="employee">Employee</option>
                    <option value="vendor_master">Vendor Master</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Attribute</Form.Label>
                  <Form.Control
                    type="text"
                    name="source_attribute"
                    value={formData.source_attribute}
                    onChange={handleChange}
                    disabled={!formData.source_association}
                    placeholder="e.g. general_type"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleChange} />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Check type="checkbox" name="is_default" label="Default" checked={formData.is_default} onChange={handleChange} className="mb-3" />
              </Col>
              <Col md={4}>
                <Form.Check type="checkbox" name="is_sortable" label="Sortable" checked={formData.is_sortable} onChange={handleChange} className="mb-3" />
              </Col>
              <Col md={4}>
                <Form.Check type="checkbox" name="is_selectable" label="Selectable" checked={formData.is_selectable} onChange={handleChange} className="mb-3" />
              </Col>
            </Row>
            <div className="d-flex justify-content-end">
              <Button variant="light-primary" onClick={onClose} className="me-2">Cancel</Button>
              <Button type="submit" variant="primary" disabled={updateMutation.isLoading}>
                {updateMutation.isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </Form>
        ) : (
          <Row>
            <Col md={6}>
              <dl className="row mb-0">
                <dt className="col-sm-5">Name:</dt>
                <dd className="col-sm-7">{tableColumn.name}</dd>
                <dt className="col-sm-5">Database Column:</dt>
                <dd className="col-sm-7"><code>{tableColumn.database_column_name || 'N/A'}</code></dd>
                <dt className="col-sm-5">Module:</dt>
                <dd className="col-sm-7">
                  {tableColumn.section_abbreviation ? (
                    <Badge bg="info">{tableColumn.section_abbreviation}</Badge>
                  ) : (
                    <Badge bg="secondary">Global</Badge>
                  )}
                </dd>
                <dt className="col-sm-5">Accessor Key:</dt>
                <dd className="col-sm-7">{tableColumn.accessor_key || 'N/A'}</dd>
                <dt className="col-sm-5">Description:</dt>
                <dd className="col-sm-7">{tableColumn.description || 'N/A'}</dd>
              </dl>
            </Col>
            <Col md={6}>
              <dl className="row mb-0">
                <dt className="col-sm-5">Display As:</dt>
                <dd className="col-sm-7">{tableColumn.display_as || 'N/A'}</dd>
                <dt className="col-sm-5">Filter Type:</dt>
                <dd className="col-sm-7">{tableColumn.filter_type || 'N/A'}</dd>
                <dt className="col-sm-5">Grouping:</dt>
                <dd className="col-sm-7">{tableColumn.grouping || 'N/A'}</dd>
                <dt className="col-sm-5">Default:</dt>
                <dd className="col-sm-7">{tableColumn.is_default ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</dd>
                <dt className="col-sm-5">Sortable:</dt>
                <dd className="col-sm-7">{tableColumn.is_sortable ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</dd>
                <dt className="col-sm-5">Selectable:</dt>
                <dd className="col-sm-7">{tableColumn.is_selectable ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</dd>
              </dl>
            </Col>
          </Row>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default TableColumnModal
